import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getForumCategories } from '@/lib/forum';
import type { ForumCategory } from '@/lib/types/forum';
import { MessageSquare, Plus, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function Forum() {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getForumCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error loading categories:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les catégories du forum.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [toast]);

  const handleNewDiscussion = () => {
    if (!currentUser) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour créer une discussion.",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    navigate('/forum/new');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-6xl mx-auto space-y-6">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-[#54075B]" />
                Forum
              </h1>
              <p className="text-gray-600">
                Échangez avec la communauté et partagez vos expériences
              </p>
            </div>
            <Button
              onClick={handleNewDiscussion}
              className="bg-[#54075B] hover:bg-[#54075B]/90 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nouvelle discussion
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/forum/category/${category.slug}`}
                  className="block"
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div
                          className="p-3 rounded-lg"
                          style={{ backgroundColor: `${category.color}20` }}
                        >
                          <MessageSquare
                            className="w-6 h-6"
                            style={{ color: category.color }}
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg mb-1">
                            {category.name}
                          </h3>
                          {category.description && (
                            <p className="text-gray-600 text-sm">
                              {category.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}